import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { verifyItemCompetitor, getItemCompetitorVerification } from "@/lib/phase3_api";
import { toast } from "sonner";
import { Crosshair, Spinner } from "@phosphor-icons/react";

export const VERDICT_STYLE = {
  pass: { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "PASS" },
  fail: { cls: "bg-red-100 text-red-900 border-red-300", label: "FAIL" },
  unclear: { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "UNCLEAR" },
};

/** Comparative gap between the analysed target and a competitor, per criterion. */
export function gapFor(mine, theirs) {
  if (mine === "pass" && theirs === "pass") return { label: "Table stakes", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" };
  if (mine === "pass") return { label: "Target leads", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" };
  if (theirs === "pass") return { label: "Competitor leads", cls: "bg-red-100 text-red-900 border-red-300" };
  return { label: "Open gap", cls: "bg-indigo-100 text-indigo-900 border-indigo-300" };
}

export function CompetitorPicker({ competitors = [], value, onChange, testid }) {
  const verified = competitors.filter((c) => c.homepage_url);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {verified.length > 0 && (
        <select
          value={verified.some((c) => c.homepage_url === value) ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-select`}
          className="border border-zinc-300 bg-white px-2 py-1.5 font-mono text-[11px] text-zinc-800 max-w-[220px]"
        >
          <option value="">Pick a competitor…</option>
          {verified.map((c) => (
            <option key={c.homepage_url} value={c.homepage_url}>
              {c.name || c.homepage_url}
            </option>
          ))}
        </select>
      )}
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="or paste any competitor URL"
        data-testid={`${testid}-input`}
        className="border border-zinc-300 bg-white px-2 py-1.5 font-mono text-[11px] text-zinc-800 flex-1 min-w-[180px]"
      />
    </div>
  );
}

/** Per build item: target verdicts vs one competitor's verdicts, criterion by criterion. */
export function ItemCompetitorCompare({ analysisId, item, competitors }) {
  const [url, setUrl] = useState("");
  const [polling, setPolling] = useState(false);
  const [queueing, setQueueing] = useState(false);

  const { data } = useQuery({
    queryKey: ["verify-competitor-item", analysisId, item.id],
    queryFn: () => getItemCompetitorVerification(analysisId, item.id),
    refetchInterval: polling ? 5000 : false,
  });

  const status = data?.status || "idle";
  const inFlight = status === "queued" || status === "running";
  const mine = data?.verification || item.verification || null;
  const runs = data?.competitor_verifications || item.competitor_verifications || [];
  const [activeUrl, setActiveUrl] = useState("");
  const active = runs.find((r) => r.target_url === activeUrl) || runs[0] || null;

  useEffect(() => {
    if (inFlight) setPolling(true);
    else if (polling) {
      setPolling(false);
      if (status === "complete") toast.success("Competitor comparison ready");
      if (status === "failed") toast.error(data?.error || "Competitor check failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const run = async () => {
    if (!url.trim()) {
      toast.error("Pick a competitor or paste a URL first");
      return;
    }
    const name = (competitors.find((c) => c.homepage_url === url) || {}).name || "";
    try {
      setQueueing(true);
      await verifyItemCompetitor(analysisId, item.id, url.trim(), name);
      setPolling(true);
      toast.info("Checking the competitor's live site against the same criteria…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start competitor check");
    } finally {
      setQueueing(false);
    }
  };

  const mineByCriterion = {};
  (mine?.checks || []).forEach((c) => { mineByCriterion[c.criterion] = c.verdict; });

  return (
    <div className="mt-3 border-t border-zinc-100 pt-3" data-testid={`competitor-verify-${item.id}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
        <Crosshair size={12} /> Competitor verify
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[240px]">
          <CompetitorPicker
            competitors={competitors}
            value={url}
            onChange={setUrl}
            testid={`competitor-picker-${item.id}`}
          />
        </div>
        <button
          onClick={run}
          disabled={queueing || inFlight}
          data-testid={`competitor-verify-button-${item.id}`}
          className="border border-zinc-950 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-60 flex items-center gap-1.5"
        >
          {inFlight || queueing ? <Spinner size={11} className="animate-spin" /> : <Crosshair size={11} />}
          {inFlight ? "Checking rival…" : "Compare"}
        </button>
      </div>

      {status === "failed" && data?.error && (
        <div className="mt-2 border border-red-300 bg-red-50 p-2 text-sm text-red-800" data-testid={`competitor-error-${item.id}`}>
          {data.error}
        </div>
      )}

      {runs.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {runs.map((r) => (
            <button
              key={r.target_url}
              onClick={() => setActiveUrl(r.target_url)}
              className={`border px-2 py-0.5 font-mono text-[10px] ${
                (active && active.target_url === r.target_url)
                  ? "border-zinc-950 bg-zinc-950 text-white"
                  : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              {r.competitor_name || r.target_url}
            </button>
          ))}
        </div>
      )}

      {!active && !inFlight && (
        <div className="mt-2 font-mono text-[10px] text-zinc-500">
          Runs the exact same acceptance criteria against a rival's public pages, then shows who already does what.
        </div>
      )}

      {active && (
        <div className="mt-3" data-testid={`competitor-result-${item.id}`}>
          <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] uppercase tracking-widest">
            <span className="text-zinc-500 normal-case tracking-normal">
              {active.competitor_name || active.target_url}
            </span>
            <span className="border border-emerald-300 bg-emerald-100 text-emerald-900 px-2 py-0.5">{active.passed} pass</span>
            <span className="border border-red-300 bg-red-100 text-red-900 px-2 py-0.5">{active.failed} fail</span>
            <span className="border border-amber-300 bg-amber-100 text-amber-900 px-2 py-0.5">{active.unclear} unclear</span>
          </div>
          {active.summary && <p className="mt-2 text-sm text-zinc-800 leading-relaxed">{active.summary}</p>}

          {!mine && (
            <div className="mt-2 font-mono text-[10px] text-amber-700">
              Run Live Verify on the target too — then this table shows the gap side by side.
            </div>
          )}

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm border border-zinc-200">
              <thead>
                <tr className="bg-zinc-50 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left p-2 border-b border-zinc-200">Criterion</th>
                  <th className="text-left p-2 border-b border-zinc-200 w-24">Target</th>
                  <th className="text-left p-2 border-b border-zinc-200 w-24">Rival</th>
                  <th className="text-left p-2 border-b border-zinc-200 w-32">Gap</th>
                </tr>
              </thead>
              <tbody>
                {(active.checks || []).map((c, i) => {
                  const mineVerdict = mineByCriterion[c.criterion] || "unclear";
                  const theirs = VERDICT_STYLE[c.verdict] || VERDICT_STYLE.unclear;
                  const ours = VERDICT_STYLE[mineVerdict] || VERDICT_STYLE.unclear;
                  const gap = gapFor(mineVerdict, c.verdict);
                  return (
                    <tr key={i} className="border-b border-zinc-100 align-top" data-testid={`gap-row-${item.id}-${i}`}>
                      <td className="p-2 text-zinc-900">
                        {c.criterion}
                        {c.evidence && <div className="mt-1 text-xs text-zinc-500">{c.evidence}</div>}
                      </td>
                      <td className="p-2">
                        {mine ? (
                          <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${ours.cls}`}>{ours.label}</span>
                        ) : (
                          <span className="font-mono text-[9px] text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${theirs.cls}`}>{theirs.label}</span>
                      </td>
                      <td className="p-2">
                        {mine && (
                          <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${gap.cls}`}>{gap.label}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
